import { useCallback, useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";

// Light / dark appearance. The mode lives on <html data-appearance>, set before
// first paint by public/index.html; this module owns every later change.
//
// Light is the default for anyone who has not chosen, whatever the OS prefers.
// Only an explicit toggle is saved, so a later visit restores that choice.
// Storage can throw (private modes, blocked cookies); the toggle still works
// for the session and the page simply starts light next time.
//
// Goo-ds keeps `data-theme="light"` underneath in both modes, and the old
// Light / Trace / Engram style selector is not part of this.

export const APPEARANCE_STORAGE_KEY = "pf-appearance";
const THEME_COLOR = { light: "#FFFFFF", dark: "#111111" };

function readAppearance() {
  if (typeof document === "undefined") return "light";
  return document.documentElement.getAttribute("data-appearance") === "dark"
    ? "dark"
    : "light";
}

function applyAppearance(mode) {
  const root = document.documentElement;
  root.setAttribute("data-appearance", mode);
  root.style.colorScheme = mode;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[mode]);
}

export function setAppearance(mode) {
  applyAppearance(mode);
  try {
    window.localStorage.setItem(APPEARANCE_STORAGE_KEY, mode);
  } catch (error) {
    // Unsaved: the choice lasts for this page load only.
  }
}

export function useAppearance() {
  const [mode, setMode] = useState(readAppearance);

  useEffect(() => {
    setMode(readAppearance());
    const observer = new MutationObserver(() => setMode(readAppearance()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-appearance"],
    });
    // Another tab changed the saved choice.
    const onStorage = (event) => {
      if (event.key !== APPEARANCE_STORAGE_KEY) return;
      if (event.newValue === "dark" || event.newValue === "light") {
        applyAppearance(event.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const toggle = useCallback(() => {
    setAppearance(readAppearance() === "dark" ? "light" : "dark");
  }, []);

  return { mode, toggle };
}

export function AppearanceToggle({ className = "" }) {
  const { mode, toggle } = useAppearance();
  const dark = mode === "dark";
  const Icon = dark ? Sun : Moon;

  return (
    <button
      type="button"
      className={`pf-appearance-toggle ${className}`.trim()}
      aria-pressed={dark}
      onClick={toggle}
    >
      <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
      <span className="pf-visually-hidden">Dark mode</span>
    </button>
  );
}
